import { MONEY_METRICS, MONEY_PERIODS, moneyMetricColumnKey, parseMoneyMetricColumnKey } from "@/lib/money-column-metrics";
import { getPeriodColumnDef, PERIOD_COLUMN_DEFS } from "@/lib/period-columns";
import { EMPTY_CELL_MARK, rubCell } from "@/lib/format";
import { FLOW_MARKS_COLUMN_KEY } from "@/lib/flow-marks-column";

/**
 * Как показывать значение колонки: деньги, процент, текст или обычное число.
 *
 * Раньше эта классификация жила внутри lib/exportExcel вместе с ExcelJS, и
 * взять её в таблицу было нельзя — потянуло бы генератор Excel в браузерную
 * сборку. Теперь она отдельно: экспорт и таблица берут один и тот же список,
 * так что цифра в файле и цифра на экране оформлены одинаково.
 *
 * Правило проекта про единый реестр колонок действует и здесь: добавляешь
 * денежную или процентную колонку — дописывай её сюда, а не в своё место.
 */

/** Колонки в рублях. */
export const CURRENCY_KEYS = new Set([
  "price", "old_price", "revenue", "orders_revenue", "revenue_30d", "revenue_7d", "revenue_14d",
  "revenue_per_unit", "orders_commission", "customer_price_avg_7d",
  "price_min", "price_marketing", "price_seller_promo", "price_retail",
  "ozon_min_competitor", "ext_min_price", "wb_price",
  "fin_revenue", "fin_commission", "fin_logistics", "fin_last_mile",
  "fin_fulfillment", "fin_mainline", "fin_return_amount",
  "cost_price",
  // Деньги в остатке — остаток × себестоимость, в строке модели складывается.
  "stock_money",
  // Реклама по периодам — 7/14/30д суммы и ставки
  "ad_spend_7d", "ad_spend_14d", "ad_spend_30d",
  "ad_revenue_7d", "ad_revenue_14d", "ad_revenue_30d",
  "ad_model_revenue_7d", "ad_model_revenue_14d", "ad_model_revenue_30d",
  "ad_total_revenue_7d", "ad_total_revenue_14d", "ad_total_revenue_30d",
  "ad_product_gmv_7d", "ad_product_gmv_14d", "ad_product_gmv_30d",
  "ad_cpc_7d", "ad_cpc_14d", "ad_cpc_30d",
  "ad_cost_per_order_7d", "ad_cost_per_order_14d", "ad_cost_per_order_30d",
  "ad_avg_bid_7d", "ad_avg_bid_14d", "ad_avg_bid_30d",
]);

const MONEY_METRIC_BY_KEY = new Map(MONEY_METRICS.map(metric => [metric.key, metric]));

/**
 * Минус в этой колонке — убыток: прибыль, маржа, ROI за 7/14/30 дней и те же
 * показатели «за период». Ячейки и строки итога красят такое значение красным;
 * у расходов и возвратов минус — обычный знак, их не трогаем.
 */
export function isLossColumnKey(key: string): boolean {
  const money = parseMoneyMetricColumnKey(key);
  if (money) return money.metric.loss === true;
  const period = getPeriodColumnDef(key);
  if (period?.source === "money") return MONEY_METRIC_BY_KEY.get(period.metric)?.loss === true;
  return false;
}

/** Деньги ли колонка: список выше плюс динамические колонки по видам услуг Ozon (money_type_*). */
export function isCurrencyColumnKey(key: string): boolean {
  return CURRENCY_KEYS.has(key) || /^money_type_\d+_(7d|14d|30d)$/.test(key);
}

/** Колонки в процентах. */
export const PERCENT_KEYS = new Set([
  "conv_tocart", "conv_search", "conv_pdp", "return_rate",
  "commission_fbo", "commission_fbs",
  "orders_share", "orders_share_7d",
  // Полнота карточки: в строке модели — средняя доля заполненного по размерам.
  "content_attrs",
  // Price indices are price ratios (1.14), not percentages (1.14%).
  // Реклама по периодам — проценты
  "ad_ctr_7d", "ad_ctr_14d", "ad_ctr_30d",
  "ad_drr_7d", "ad_drr_14d", "ad_drr_30d",
  "ad_total_drr_7d", "ad_total_drr_14d", "ad_total_drr_30d",
  "ad_shop_drr_7d", "ad_shop_drr_14d", "ad_shop_drr_30d",
  "ad_conv_cart_7d", "ad_conv_cart_14d", "ad_conv_cart_30d",
  "ad_conv_order_7d", "ad_conv_order_14d", "ad_conv_order_30d",
]);

// Показатели денег по товару — из единого списка: деньги и проценты попадают
// в те же наборы, что остальные колонки.
for (const metric of MONEY_METRICS) {
  for (const period of MONEY_PERIODS) {
    const key = moneyMetricColumnKey(metric.key, period);
    if (metric.kind === "money") CURRENCY_KEYS.add(key);
    else if (metric.kind === "pct") PERCENT_KEYS.add(key);
  }
}
// Колонки «за период» (lib/period-columns.ts) — так же, по виду показателя.
for (const def of PERIOD_COLUMN_DEFS) {
  if (def.kind === "money") CURRENCY_KEYS.add(def.key);
  else if (def.kind === "pct") PERCENT_KEYS.add(def.key);
}

/** Колонки-текст: складывать их бессмысленно, итога у них не бывает. */
export const TEXT_KEYS = new Set([
  FLOW_MARKS_COLUMN_KEY,
  "name", "offer_id", "product_id", "status", "photo", "primary_image",
  "attr_brand", "attr_material", "attr_color", "attr_custom_color",
  "attr_size", "attr_gender", "attr_season", "attr_common_article", "attr_country",
  "barcodes", "sources", "description_category_id", "type_id",
  "errors_text",
  "vat", "service_type", "price_index",
  "supply_last_date", "supply_in_transit_date", "supply_cluster",
  // Заметка к товару: текст, выравнивается влево, итога у него не бывает.
  "flow_note",
  // Главный поисковый запрос — текст, итога у него не бывает.
  "top_query",
  // Склейка — название модели, текст; итог у строки модели — она сама.
  "product_group",
  // Партия — полоска долей, а не число: складывать нечего.
  "batch_share",
  // Поставщик — тоже текст. В строке модели показываем его отдельно (у всех
  // размеров он один), но «итога» в смысле числа у него нет.
  "supplier",
  // Матрица кластеров — ряд чипов слева направо, как текст; итога нет.
  "cluster_matrix",
]);

/** Колонки, у которых итог по выборке не имеет смысла: картинки, графики,
 *  бейджи и «мои данные». Для них показываем прочерк, а не число. */
// Цена по дням: сумма цен по выборке ничего не значит (остаток и расход на
// рекламу по дням складываются честно — это те же колонки «Остаток» и «Расход»).
const NO_TOTAL_PREFIXES = ["attr_", "promo_", "action_diary_day:", "price_week_day:", "price_week_sum:"];
const NO_TOTAL_KEYS = new Set([
  "photo", "primary_image", "row_select", "task", "action_diary", "orders_weeks", "stock_weeks", "price_weeks", "ads_weeks",
  "orders_chart", "orders_calendar", "ctr_calendar", "customer_price_chart",
  "stock_history", "stock_orders_combo", "wb_orders_calendar", "wb_stock_orders_combo",
  "views_spark", "tocart_spark", "conv_spark",
  "abc_revenue_7d", "abc_revenue_14d", "abc_revenue_30d",
  "abc_orders_7d", "abc_orders_14d", "abc_orders_30d",
  "top_yesterday", "top_7d", "warehouse_turnover", "is_super",
  // Акция заводится НА РАЗМЕР: у одного размера она есть, у соседнего нет.
  // Сумма единиц по выборке («300») выглядела как показатель товара, хотя это
  // просто счётчик SKU в акциях. Соседняя колонка «В акции» (promo_presence)
  // прочерк показывала всегда — приставка promo_ исключена целиком; теперь обе
  // ведут себя одинаково.
  "has_promotion",
  // Видео и rich заводятся НА КАРТОЧКУ, а строка модели объединяет несколько
  // карточек-размеров. Любое число тут врёт: сумма «8» — это просто счётчик, а
  // не показатель модели. Поэтому в строке модели прочерк.
  "content_video",
]);

/**
 * Колонка про участие в акции. Акция заводится НА РАЗМЕР: у одного размера она
 * есть, у соседнего нет. Поэтому в строках, которые представляют несколько
 * размеров (итог модели, итог таблицы), такая колонка обязана быть пустой —
 * иначе туда попадает бейдж случайного размера-представителя.
 */
export function isPromotionColumn(key: string): boolean {
  return key === "has_promotion" || key.startsWith("promo_");
}

export function hasColumnTotal(key: string): boolean {
  if (TEXT_KEYS.has(key) || NO_TOTAL_KEYS.has(key)) return false;
  if (NO_TOTAL_PREFIXES.some(prefix => key.startsWith(prefix))) return false;
  return true;
}

/**
 * Формат итога по колонке — русские разряды, ₽ у денег, % у процентов.
 * Дробную часть показываем только там, где она осмысленна: у процентов и у
 * денег до тысячи. Итог «1 234 567,00 ₽» читается хуже, чем «1 234 567 ₽».
 */
export function formatColumnTotal(key: string, value: number): string {
  if (!Number.isFinite(value)) return EMPTY_CELL_MARK;
  if (PERCENT_KEYS.has(key)) {
    return `${value.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}%`;
  }
  // Деньги — как в ячейках: целые, тонкий пробел, минус, «₽» справа, ноль — тире.
  if (isCurrencyColumnKey(key)) return rubCell(value);
  const digits = Number.isInteger(value) ? 0 : Math.abs(value) < 100 ? 1 : 0;
  return value.toLocaleString("ru-RU", { maximumFractionDigits: digits });
}
